import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supermarketService, Supermarket } from '../services/supermarketService';
import { useAuth } from '../contexts/AuthContext';
import { extractAndSaveCustomCategories } from '../utils/supermarketCategories';
import Logo from '../components/Logo';
import BurgerMenu from '../components/BurgerMenu';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { Store, MapPin, Users, AlertTriangle, Edit, Trash2, Plus, Lock, Unlock } from 'lucide-react';

export default function SupermarketList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [supermarkets, setSupermarkets] = useState<Supermarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  useEffect(() => {
    loadSupermarkets();
  }, []);

  const loadSupermarkets = async () => {
    try {
      setLoading(true);
      const data = await supermarketService.getAll();
      setSupermarkets(data);
      
      // Extract and save custom categories from all supermarkets
      data.forEach(supermarket => {
        extractAndSaveCustomCategories(supermarket);
      });
    } catch (error) {
      console.error('Error loading supermarkets:', error);
      setToast({ message: 'Fehler beim Laden der Supermärkte', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (supermarket: Supermarket) => {
    if (supermarket.firstCreatedById !== user?.id) {
      setToast({ message: 'Du kannst nur deine eigenen Supermärkte löschen', type: 'error' });
      return;
    }

    setConfirmDialog({
      title: 'Supermarkt löschen',
      message: `Möchtest du "${supermarketService.getShortAddress(supermarket)}" wirklich löschen?`,
      onConfirm: async () => {
        try {
          await supermarketService.delete(supermarket.id);
          setToast({ message: 'Supermarkt gelöscht', type: 'success' });
          loadSupermarkets();
        } catch (error: any) {
          setToast({ 
            message: error.response?.data?.error || 'Fehler beim Löschen', 
            type: 'error' 
          });
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleToggleLock = async (supermarket: Supermarket) => {
    try {
      await supermarketService.toggleUserLock(supermarket.id, !supermarket.userLocked);
      setToast({ 
        message: supermarket.userLocked ? 'Vorlage entsperrt' : 'Vorlage gesperrt', 
        type: 'success' 
      });
      loadSupermarkets();
    } catch (error: any) {
      setToast({ message: error.response?.data?.error || 'Fehler beim Aktualisieren', type: 'error' });
    }
  };

  const mySupermarkets = supermarkets.filter(s => s.firstCreatedById === user?.id);
  const communitySupermarkets = supermarkets.filter(s => s.firstCreatedById !== user?.id);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'var(--gray-50)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: 'var(--gray-600)' }}>Lade Supermärkte...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)' }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: 'var(--bg-primary)',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <Logo variant="dark" />
            <BurgerMenu currentPage="supermarkets" />
          </div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gray-900)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>🏪</span>
            Meine Supermärkte
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container" style={{ paddingTop: '1rem' }}>
        {/* Create Button */}
        <button
          onClick={() => navigate('/supermarkets/create')}
          style={{
            width: '100%',
            padding: '1rem',
            backgroundColor: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-dark)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary)'}
        >
          <Plus size={20} />
          Neuen Supermarkt anlegen
        </button>

        {/* My Supermarkets */}
        {mySupermarkets.length > 0 && (
          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--gray-800)',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Store size={20} />
              Meine Supermärkte ({mySupermarkets.length})
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {mySupermarkets.map(supermarket => (
                <SupermarketCard
                  key={supermarket.id}
                  supermarket={supermarket}
                  isOwner={true}
                  onEdit={() => navigate(`/supermarkets/${supermarket.id}/edit`)}
                  onDelete={() => handleDelete(supermarket)}
                  onToggleLock={() => handleToggleLock(supermarket)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Community Supermarkets */}
        {communitySupermarkets.length > 0 && (
          <section>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--gray-800)',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Users size={20} />
              Community-Supermärkte ({communitySupermarkets.length})
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {communitySupermarkets.map(supermarket => (
                <SupermarketCard
                  key={supermarket.id}
                  supermarket={supermarket}
                  isOwner={false}
                  onEdit={() => navigate(`/supermarkets/${supermarket.id}/edit`)}
                  onToggleLock={undefined}
                />
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {supermarkets.length === 0 && (
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            boxShadow: 'var(--shadow)'
          }}>
            <Store size={48} style={{ color: 'var(--gray-400)', margin: '0 auto 1rem' }} />
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-800)' }}>
              Noch keine Supermärkte
            </h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
              Lege deinen ersten Supermarkt an und nutze die geführte Einkaufsroute!
            </p>
            <button
              onClick={() => navigate('/supermarkets/create')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Jetzt anlegen
            </button>
          </div>
        )}
      </main>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

interface SupermarketCardProps {
  supermarket: Supermarket;
  isOwner: boolean;
  onEdit: () => void;
  onDelete?: () => void;
  onToggleLock?: () => void;
}

function SupermarketCard({ supermarket, isOwner, onEdit, onDelete, onToggleLock }: SupermarketCardProps) {
  const hasReports = (supermarket._count?.reports || 0) > 0;

  return (
    <div style={{
      backgroundColor: 'var(--bg-primary)',
      borderRadius: '12px',
      padding: '1.25rem',
      boxShadow: 'var(--shadow)',
      border: supermarket.isLocked ? '2px solid var(--error)' : hasReports ? '2px solid var(--warning)' : 'none'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '0.75rem'
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: '0 0 0.25rem 0',
            fontSize: '1.125rem',
            fontWeight: '600',
            color: 'var(--gray-900)'
          }}>
            {supermarket.chain} {supermarket.name && `(${supermarket.name})`}
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            color: 'var(--gray-600)',
            fontSize: '0.875rem'
          }}>
            <MapPin size={14} />
            <span>{supermarket.street}, {supermarket.zipCode} {supermarket.city}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isOwner && onToggleLock && (
            <button
              onClick={onToggleLock}
              style={{
                padding: '0.5rem',
                backgroundColor: supermarket.userLocked ? 'var(--primary)' : 'transparent',
                border: `1px solid ${supermarket.userLocked ? 'var(--primary)' : 'var(--gray-300)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                color: supermarket.userLocked ? 'white' : 'var(--gray-700)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={supermarket.userLocked ? 'Vorlage entsperren' : 'Vorlage sperren'}
            >
              {supermarket.userLocked ? <Lock size={16} /> : <Unlock size={16} />}
            </button>
          )}
          {/* Show lock indicator for non-owners */}
          {!isOwner && supermarket.userLocked && (
            <div
              style={{
                padding: '0.5rem',
                backgroundColor: 'var(--gray-200)',
                border: '1px solid var(--gray-300)',
                borderRadius: '6px',
                color: 'var(--gray-600)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Vorlage ist gesperrt - kann nicht bearbeitet werden"
            >
              <Lock size={16} />
            </div>
          )}
          <button
            onClick={onEdit}
            disabled={!isOwner && supermarket.userLocked}
            style={{
              padding: '0.5rem',
              backgroundColor: 'transparent',
              border: '1px solid var(--gray-300)',
              borderRadius: '6px',
              cursor: (!isOwner && supermarket.userLocked) ? 'not-allowed' : 'pointer',
              color: (!isOwner && supermarket.userLocked) ? 'var(--gray-400)' : 'var(--gray-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (!isOwner && supermarket.userLocked) ? 0.5 : 1
            }}
            title={(!isOwner && supermarket.userLocked) ? 'Vorlage ist gesperrt' : 'Bearbeiten'}
          >
            <Edit size={16} />
          </button>
          {isOwner && onDelete && (
            <button
              onClick={onDelete}
              style={{
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--error)',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'var(--error)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Löschen"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Warnings */}
      {supermarket.isLocked && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid var(--error)',
          borderRadius: '6px',
          padding: '0.75rem',
          marginBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem',
          color: 'var(--error)'
        }}>
          <AlertTriangle size={16} />
          <span>Gesperrt: {supermarket.lockReason}</span>
        </div>
      )}

      {hasReports && !supermarket.isLocked && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid var(--warning)',
          borderRadius: '6px',
          padding: '0.75rem',
          marginBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem',
          color: '#92400e'
        }}>
          <AlertTriangle size={16} />
          <span>{supermarket._count!.reports} Problem(e) gemeldet</span>
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        fontSize: '0.875rem',
        color: 'var(--gray-600)'
      }}>
        <div>
          <span style={{ fontWeight: '600' }}>{supermarket.zones.length}</span> Zonen
        </div>
        <div>
          <span style={{ fontWeight: '600' }}>{supermarket._count?.lists || 0}</span> Listen
        </div>
        <div>
          <span style={{ fontWeight: '600' }}>{supermarket.usageCount}</span> Verwendungen
        </div>
      </div>

      {/* Creator Info */}
      <div style={{
        marginTop: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--gray-200)',
        fontSize: '0.75rem',
        color: 'var(--gray-500)'
      }}>
        {isOwner ? (
          <span>Von dir erstellt</span>
        ) : (
          <span>Von {supermarket.firstCreatedBy?.name || 'Unbekannt'}</span>
        )}
        {supermarket.lastEditedBy && supermarket.lastEditedAt && (
          <span> • Zuletzt bearbeitet von {supermarket.lastEditedBy.name} </span>
        )}
      </div>
    </div>
  );
}
